/**
 * Idempotency-Key generation.
 *
 * Sent on every mutating request (POST / PATCH / PUT / DELETE). The backend
 * MUST treat two requests with the same key in close succession as the same
 * operation — protecting institutional flows from a double-click on
 * "Submit Order" creating two orders. Until the backend implements this
 * (Backend ask: "Honour `Idempotency-Key` header on mutating endpoints,
 * dedup window 60s, return the original response on repeat"), the key is
 * still emitted so the backend team can see the traffic shape on day one.
 *
 * The key is a UUID v4 — globally unique, no leakage of internal state.
 *
 * The api client allows callers to override the key when they need to: e.g.,
 * a retry of an already-failed POST should reuse the SAME key so the backend
 * dedups the duplicate write attempt. The default is a fresh key per call.
 */

export const newIdempotencyKey = (): string => {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

export const IDEMPOTENT_METHODS: ReadonlySet<string> = new Set(['GET', 'HEAD', 'OPTIONS']);

export const requiresIdempotencyKey = (method: string): boolean =>
  !IDEMPOTENT_METHODS.has(method.toUpperCase());
